import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, DragEvent, RefObject } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTime } from '../lib/util';
import { ApiProblem } from '../api/client';
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_PDF_TYPE,
  MAX_EVIDENCE_FILE_BYTES,
  downloadEvidenceFile,
  evidenceImageDataUrl,
  attachedTo,
  isEvidenceImage,
  listEvidenceFiles,
  removeEvidenceFile,
  uploadEvidenceFile,
  type EvidenceFile,
  type EvidenceTarget,
} from '../api/evidenceFiles';
import { Dialog } from './Dialog';
import { getCurrentUser } from '../auth/useUser';

// #774 — screenshots and PDFs on a piece of evidence: a KYC evidence record, or
// the SHAFT, IP and Trust review sections. One component for all four, so the
// paste, drop and browse behaviour — and the rule that an image is shown only
// as a `data:` URL read with the bearer token — cannot drift between tabs.
//
// Two modes:
//   * LIVE (`target` given) — a file goes to the API the moment it is added,
//     and the stored files are listed with thumbnails, enlarge and remove.
//   * STAGED (`onStagedChange` given) — the KYC "Add Evidence Record" form. The
//     record does not exist until it is saved, and the API refuses a file for a
//     record it cannot find, so files are held here. On save the tab hands them
//     to the new record's LIVE instance as `queued`. The case PATCH that saves
//     the record is chained per case and not awaitable from the tab, so a queued
//     upload that reaches the API first is answered 422 with the code
//     `evidence_record_missing` and retried on a short schedule; no other
//     refusal is retried (an infected file is never re-sent).
//
// The browser's declared type is checked here only to tell the analyst early;
// the server decides by the bytes and its answer (415) is shown if it differs.

/** The files on the clipboard of a paste event — screenshots, usually. */
export function filesFromClipboard(e: { clipboardData: DataTransfer | null }): File[] {
  const items = Array.from(e.clipboardData?.items ?? []);
  return items
    .filter((i) => i.kind === 'file')
    .map((i) => i.getAsFile())
    .filter((f): f is File => f !== null);
}

/** Why a file will not be accepted, or null. */
export function fileProblem(file: Pick<File, 'type' | 'size' | 'name'>): string | null {
  if (file.type !== EVIDENCE_PDF_TYPE && !isEvidenceImage(file.type)) {
    return `${file.name || 'The pasted item'} is not a PNG, JPEG or WebP image or a PDF.`;
  }
  if (file.size > MAX_EVIDENCE_FILE_BYTES) return `${file.name} is larger than 50 MB.`;
  if (file.size === 0) return `${file.name || 'The pasted item'} is empty.`;
  return null;
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** A name for a pasted screenshot, which arrives as "image.png" or with none. */
export function nameFor(file: File): string {
  const generic = /^image\.(png|jpe?g|webp)$/i.test(file.name);
  if (file.name && !generic) return file.name;
  const ext = file.type === 'image/jpeg' ? 'jpg' : file.type.split('/')[1] ?? 'png';
  return `pasted-${new Date().toISOString().replace(/[:.]/g, '-')}.${ext}`;
}

/**
 * The `code` the API puts on the 422 for a KYC evidence record it cannot find
 * (EVIDENCE_RECORD_MISSING in apps/api/src/routes/evidence-files.ts). A COPY,
 * pinned to that source by EvidenceAttachments.test.tsx (#589).
 */
export const EVIDENCE_RECORD_MISSING = 'evidence_record_missing';

/** How long a queued upload waits, between tries, for its record's save to land. */
export const RECORD_SAVE_RETRY_DELAYS_MS: readonly number[] = [500, 1000, 2000, 4000, 8000];

function recordNotSavedYet(err: unknown): boolean {
  return err instanceof ApiProblem && err.status === 422 && err.extensions.code === EVIDENCE_RECORD_MISSING;
}

function problemText(err: unknown): string {
  if (err instanceof ApiProblem) return err.detail ?? err.title;
  return err instanceof Error ? err.message : String(err);
}

/** The case's files, loaded once per case and reloaded after a change. */
export function useEvidenceFiles(caseId: string): {
  files: EvidenceFile[];
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [files, setFiles] = useState<EvidenceFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const current = useRef(caseId);
  current.current = caseId;
  const refresh = useCallback(async () => {
    try {
      const next = await listEvidenceFiles(caseId);
      if (current.current === caseId) { setFiles(next); setError(null); }
    } catch (err) {
      if (current.current === caseId) setError(`Could not load attached files: ${problemText(err)}`);
    }
  }, [caseId]);
  useEffect(() => { setFiles([]); void refresh(); }, [refresh]);
  return { files, error, refresh };
}

function Thumbnail({ caseId, file, onOpen }: { caseId: string; file: EvidenceFile; onOpen: (src: string) => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    evidenceImageDataUrl(caseId, file.id)
      .then((url) => { if (live) setSrc(url); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [caseId, file.id]);
  if (failed) return <span style={{ fontSize: 11, color: C.danger }}>Could not load image</span>;
  if (!src) return <span style={{ fontSize: 11, color: C.textMuted }}>Loading…</span>;
  return (
    <button
      type="button"
      onClick={() => onOpen(src)}
      aria-label={`Enlarge ${file.filename}`}
      style={{ ...styles.btnUnstyled, display: 'block' }}
    >
      <img src={src} alt={file.filename} style={{ maxWidth: 120, maxHeight: 80, borderRadius: 4, border: `1px solid ${C.border}`, display: 'block' }} />
    </button>
  );
}

type Props = {
  caseId: string;
  /** Heading shown above the drop zone. */
  label?: string;
  /** Legal, or anyone else who cannot write the case: view and download only. */
  readOnly?: boolean;
} & (
  | {
    target: EvidenceTarget;
    /** The stored files for this target (already filtered). */
    files: EvidenceFile[];
    /** Called after an upload or a removal, to reload the list. */
    onChanged: () => void | Promise<void>;
    /** Files staged before this record was saved, to upload now (see STAGED). */
    queued?: File[];
    /** Called as soon as `queued` has been taken, so the tab can drop them. */
    onQueuedTaken?: () => void;
    /** Called with the names of queued files given up on because their record
     *  never appeared (its save lost a 409). This instance is usually gone by
     *  then, with its inline message, so the tab says it somewhere that stays. */
    onQueuedLost?: (filenames: string[]) => void;
    staged?: never;
    onStagedChange?: never;
    pasteTarget?: never;
  }
  | {
    target?: never;
    files?: never;
    onChanged?: never;
    queued?: never;
    onQueuedTaken?: never;
    onQueuedLost?: never;
    staged: File[];
    onStagedChange: (files: File[]) => void;
    /** The whole form: a paste anywhere in it (outside a text field's own
     *  text paste) stages the clipboard's files. */
    pasteTarget?: RefObject<HTMLElement | null>;
  }
);

export function EvidenceAttachments(props: Props) {
  const { caseId, label = 'Attachments', readOnly = false } = props;
  const live = props.target !== undefined;
  const [problem, setProblem] = useState<string | null>(null);
  const [uploading, setUploading] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [enlarged, setEnlarged] = useState<{ file: EvidenceFile; src: string } | null>(null);
  const [removing, setRemoving] = useState<EvidenceFile | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = async (f: File, waitForRecord: boolean): Promise<void> => {
    for (let attempt = 0; ; attempt += 1) {
      try {
        await uploadEvidenceFile(caseId, props.target!, f, nameFor(f));
        return;
      } catch (err) {
        if (!waitForRecord || !recordNotSavedYet(err) || attempt >= RECORD_SAVE_RETRY_DELAYS_MS.length) throw err;
        await new Promise((resolve) => setTimeout(resolve, RECORD_SAVE_RETRY_DELAYS_MS[attempt]));
      }
    }
  };

  const accept = async (incoming: File[], waitForRecord = false) => {
    if (incoming.length === 0) return;
    const problems = incoming.map(fileProblem).filter((p): p is string => p !== null);
    const ok = incoming.filter((f) => fileProblem(f) === null);
    setProblem(problems.length > 0 ? problems.join(' ') : null);
    if (ok.length === 0) return;
    if (!live) {
      props.onStagedChange!([...props.staged!, ...ok]);
      return;
    }
    setUploading((n) => n + ok.length);
    const failures: string[] = [];
    const lost: string[] = [];
    // One at a time: the API accepts only a few uploads at once per process.
    for (const f of ok) {
      try {
        await upload(f, waitForRecord);
      } catch (err) {
        failures.push(`${f.name || 'Pasted image'}: ${problemText(err)}`);
        if (waitForRecord && recordNotSavedYet(err)) lost.push(f.name || 'Pasted image');
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (failures.length > 0) setProblem([...problems, ...failures].join(' '));
    if (lost.length > 0) props.onQueuedLost?.(lost);
    await props.onChanged!();
  };

  // The latest `accept`, for the native listeners below.
  const acceptRef = useRef(accept);
  acceptRef.current = accept;

  const { queued, onQueuedTaken, pasteTarget } = props;
  // The array last taken. StrictMode runs this effect twice before the tab's
  // onQueuedTaken has re-rendered it away, which uploaded every file twice.
  const takenRef = useRef<File[] | null>(null);
  useEffect(() => {
    if (!live || !queued || queued.length === 0 || takenRef.current === queued) return;
    takenRef.current = queued;
    onQueuedTaken?.();
    void acceptRef.current(queued, true);
  }, [live, queued, onQueuedTaken]);

  useEffect(() => {
    const el = pasteTarget?.current;
    if (!el || readOnly) return;
    const listener = (e: globalThis.ClipboardEvent) => {
      const files = filesFromClipboard(e);
      if (files.length === 0) return;
      e.preventDefault();
      void acceptRef.current(files);
    };
    el.addEventListener('paste', listener);
    return () => el.removeEventListener('paste', listener);
  }, [pasteTarget, readOnly]);

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    // Inside a form with its own paste target, that listener has it already.
    if (readOnly || pasteTarget) return;
    const files = filesFromClipboard(e);
    if (files.length === 0) return; // plain text: leave it to the field it was pasted into
    e.preventDefault();
    void accept(files);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (readOnly) return;
    void accept(Array.from(e.dataTransfer?.files ?? []));
  };

  const confirmRemove = async () => {
    if (!removing) return;
    if (!reason.trim()) { setReasonError('A reason is required — it is kept in the audit trail.'); return; }
    try {
      await removeEvidenceFile(caseId, removing.id, reason.trim());
      setRemoving(null);
      setReason('');
      setReasonError(null);
      await props.onChanged!();
    } catch (err) {
      setReasonError(problemText(err));
    }
  };

  const stored = live ? props.files! : [];
  const staged = live ? [] : props.staged!;

  return (
    <div data-testid="evidence-attachments" style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.textSecondary, marginBottom: 6 }}>{label}</div>
      {stored.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
          {stored.map((f) => (
            <div key={f.id} data-testid="evidence-file" style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: 6, background: C.bgCard, fontSize: 11, maxWidth: 180 }}>
              {isEvidenceImage(f.contentType)
                ? <Thumbnail caseId={caseId} file={f} onOpen={(src) => setEnlarged({ file: f, src })} />
                : (
                  <button
                    type="button"
                    style={{ ...styles.btnUnstyled, color: C.goldText, textAlign: 'left' }}
                    onClick={() => { downloadEvidenceFile(caseId, f).catch((err) => setProblem(problemText(err))); }}
                  >📄 Download</button>
                )}
              <div title={f.filename} style={{ marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.filename}</div>
              <div style={{ color: C.textDim }}>{fmtBytes(f.byteLength)} · {f.uploadedByName ?? 'Unknown'} · {fmtDateTime(f.uploadedAt)}</div>
              {!readOnly && (
                <button
                  type="button"
                  aria-label={`Remove ${f.filename}`}
                  style={{ ...styles.btnUnstyled, color: C.danger, fontSize: 11, marginTop: 2 }}
                  onClick={() => { setRemoving(f); setReason(''); setReasonError(null); }}
                >Remove</button>
              )}
            </div>
          ))}
        </div>
      )}
      {staged.length > 0 && (
        <ul data-testid="evidence-staged" style={{ listStyle: 'none', padding: 0, margin: '0 0 8px', fontSize: 11 }}>
          {staged.map((f, i) => (
            <li key={`${f.name}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>{f.type === EVIDENCE_PDF_TYPE ? '📄' : '🖼'} {nameFor(f)} ({fmtBytes(f.size)})</span>
              <button
                type="button"
                aria-label={`Do not attach ${f.name || 'pasted image'}`}
                style={{ ...styles.btnUnstyled, color: C.danger }}
                onClick={() => props.onStagedChange!(staged.filter((_, j) => j !== i))}
              >×</button>
            </li>
          ))}
        </ul>
      )}
      {readOnly && stored.length === 0 && (
        <div style={{ fontSize: 11, color: C.textMuted }}>No files attached.</div>
      )}
      {!readOnly && (
        <div
          data-testid="evidence-drop-zone"
          tabIndex={0}
          role="group"
          aria-label={`${label}: paste, drop or browse for screenshots and PDFs`}
          onPaste={onPaste}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          style={{
            border: `1px dashed ${dragOver ? C.goldText : C.border}`,
            borderRadius: 6,
            padding: '8px 10px',
            fontSize: 11,
            color: C.textMuted,
            background: dragOver ? C.goldBg : 'transparent',
          }}
        >
          Paste (Ctrl+V), drop, or{' '}
          <button type="button" style={{ ...styles.btnUnstyled, color: C.goldText, textDecoration: 'underline' }} onClick={() => inputRef.current?.click()}>
            browse
          </button>{' '}
          — PNG, JPEG, WebP or PDF, up to 50 MB each. Files are scanned for malware before they are stored.
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={EVIDENCE_ACCEPT}
            data-testid="evidence-file-input"
            style={{ display: 'none' }}
            onChange={(e) => { void accept(Array.from(e.target.files ?? [])); e.target.value = ''; }}
          />
        </div>
      )}
      {uploading > 0 && <div role="status" style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>Uploading and scanning {uploading} file{uploading > 1 ? 's' : ''}…</div>}
      {problem && <div role="alert" style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>{problem}</div>}

      {enlarged && (
        <Dialog title={enlarged.file.filename} onClose={() => setEnlarged(null)}>
          <img src={enlarged.src} alt={enlarged.file.filename} style={{ maxWidth: '80vw', maxHeight: '70vh', display: 'block', marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => { downloadEvidenceFile(caseId, enlarged.file).catch((err) => setProblem(problemText(err))); }}>Download</button>
            <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => setEnlarged(null)}>Close</button>
          </div>
        </Dialog>
      )}
      {removing && (
        <Dialog title="Remove attached file?" titleStyle={{ color: C.danger }} onClose={() => setRemoving(null)}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            <strong>{removing.filename}</strong> will no longer show on this case. The file itself is kept for the
            retention period, and the audit trail records who removed it and why.
          </div>
          <label htmlFor="evidence-remove-reason" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>Reason *</label>
          <textarea
            id="evidence-remove-reason"
            style={{ ...styles.textarea, fontSize: 12, minHeight: 60, ...(reasonError ? { borderColor: C.danger } : {}) }}
            value={reason}
            aria-invalid={reasonError ? true : undefined}
            onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(null); }}
          />
          {reasonError && <div role="alert" style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>{reasonError}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={{ ...styles.btn, ...styles.btnDanger }} onClick={() => { void confirmRemove(); }}>Remove file</button>
            <button style={{ ...styles.btn, ...styles.btnOutline }} onClick={() => setRemoving(null)}>Cancel</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

/**
 * The SHAFT, IP and Trust review sections' attachments: the case's files for
 * that section, loaded here, uploaded and removed in place. Read-only for
 * legal, which reads the files of the cases it can see and writes none (the
 * API refuses legal an upload or a removal with 403).
 */
export function SectionEvidenceAttachments({ caseId, section, readOnly = !!getCurrentUser()?.isLegal }: {
  caseId: string;
  section: 'shaft' | 'ip' | 'trust';
  readOnly?: boolean;
}) {
  const { files, error, refresh } = useEvidenceFiles(caseId);
  const target: EvidenceTarget = { section };
  return (
    <div style={{ marginTop: 16 }}>
      <EvidenceAttachments
        caseId={caseId}
        label="Evidence files — screenshots and PDFs"
        target={target}
        files={files.filter((f) => attachedTo(f, target))}
        onChanged={refresh}
        readOnly={readOnly}
      />
      {error && <div role="alert" style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>{error}</div>}
    </div>
  );
}
